import { useMemo, useRef } from 'react'
import { Button } from '@heroui/react'
import { useStaffText } from '../ui/StaffText'
import { CALLED_SCREEN_RULES } from '../lib/callPage'
import { useCallQueue } from '../lib/useCallQueue'
import { calledSeconds, hasCalled } from './calledScreen'
import { sampleNumber } from './CounterStage'
import { DisplayStage } from './DisplayStage'
import { sampleBindings } from './sample'
import { usePlayedDocument } from './previewPlaylists'
import { tickerLines } from './screenBindings'
import type { LayoutDocument } from './types'

/**
 * A main display as its hall sees it, with sample values and its playlists playing.
 * Preview call (a design with a Called screen) calls the next sample number: the
 * Called screen shows for the design's time, then the board again. Pressed quickly,
 * the calls queue as they would on the wall.
 */
export function DisplayPreview({
  document: doc,
  branchName,
  language,
  languages,
  assetURL,
}: {
  document: LayoutDocument
  branchName: string
  language: string
  languages?: readonly string[]
  assetURL: (id: string) => string
}) {
  const { say } = useStaffText()
  const played = usePlayedDocument(doc)
  const [queue, enqueue] = useCallQueue(calledSeconds(doc), CALLED_SCREEN_RULES)
  const made = useRef<Array<{ id: string; number: string; desk: string; deskNumber?: string }>>([])
  const sample = useMemo(() => sampleBindings(branchName, language, languages), [branchName, language, languages])
  const desk = sample.deskName ?? ''
  const deskNumber = sample.deskNumber
  const shown = queue.showing
  const calls = [...made.current, ...(sample.calls ?? [])]

  const call = () => {
    const n = made.current.length
    // After the board's own A-042.
    const number = sampleNumber(n + 1)
    made.current = [{ id: `p${n}`, number, desk, deskNumber }, ...made.current]
    enqueue({ key: `p${n}`, number, desk })
  }

  return (
    <div className="studio-preview">
      {hasCalled(doc) && (
        <div className="studio-preview__bar">
          <Button variant="secondary" size="sm" onPress={call}>
            {say('designs.ed.previewCall')}
          </Button>
        </div>
      )}
      <div className="studio-preview__screen">
        <DisplayStage
          document={played}
          bindings={{
            ...sample,
            language,
            assetURL,
            preview: true,
            playing: true,
            ticker: tickerLines(played, language),
            calls,
          }}
          call={shown ? { number: shown.number, desk: shown.desk, deskNumber: sample.deskNumber, service: sample.serviceName } : null}
        />
      </div>
    </div>
  )
}
